import { LAYERS } from '@components/Maps/Mapbox/config/Layers'
import Logger from '@logger'
import { type AnyLayer, type AnySourceData, type Map } from 'mapbox-gl'

const logger = Logger.create('Mapbox')

export class MapboxAwaiter {
  private readonly _ids = new Set<NodeJS.Timeout>()

  async waitFor (condition: () => boolean, name: string, timeout = 20000) {
    return new Promise<void>((resolve, reject) => {
      const endAt = Date.now() + timeout

      const id = setInterval(() => {
        let success = false
        let errorMessage = ''

        if (Date.now() > endAt) {
          errorMessage = `Waiting for ${name} timed out after ${timeout} milliseconds`
        } else if (condition()) {
          success = true
        }

        if (errorMessage || success) {
          clearInterval(id)
          this._ids.delete(id)
        }

        if (errorMessage) {
          reject(new Error(errorMessage))
        } else if (success) {
          resolve()
        }
      }, 40)

      this._ids.add(id)
    })
  }

  async waitForStyleLoaded (map: Map | null) {
    return await this.waitFor(() => !!map?.isStyleLoaded(), 'style loaded')
  }

  async waitForSourceAdded (map: Map | null, sourceId: string) {
    return await this.waitFor(
      () => !!map?.isStyleLoaded() && !!map?.isSourceLoaded(sourceId),
      `source ${sourceId} added`
    )
  }

  async addLayer (map: Map | null | undefined, layer: AnyLayer, source: AnySourceData) {
    if (!map) {
      return Promise.resolve()
    }

    await this.waitForStyleLoaded(map)

    const src = map.getSource(layer.id)

    if (src && source.type === 'geojson' && source.data) {
      logger.verbose(`will set data for ${layer.id}`)
      try {
        // @ts-expect-error leave as is
        src.setData(source.data)
      } catch (err) {
        logger.verbose(`could not set data for ${layer.id}: [${JSON.stringify(source.data)}]`, err)
      }
    } else {
      if (src) {
        map.removeSource(layer.id)
      }

      logger.verbose(`will add source ${layer.id}`)
      map.addSource(layer.id, source)

      await this.waitFor(() => !!map.getSource(layer.id), `source ${source} added`)

      logger.verbose(`will add layer ${layer.id}`)
      map.addLayer(layer)
    }

    await this.waitForSourceAdded(map, layer.id)
    if (layer.id !== 'route' && map.getLayer('route')) {
      map.moveLayer(layer.id, 'route')
    }
  }

  async addDayNightLayer (map: Map | null) {
    return await this.addLayer(map, LAYERS.dayNightLayer.config, LAYERS.dayNightLayer.source)
  }

  async addDepthLayer (map: Map | null) {
    return await this.addLayer(map, LAYERS.depthLayer.config, LAYERS.depthLayer.source)
  }

  cancelAll () {
    this._ids.forEach(id => {
      clearInterval(id)
    })
    this._ids.clear()
  }
}
